import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { MessageCircle, CreditCard, ShieldCheck, Star, CheckCircle2, ChevronDown, Play, BadgeCheck, Zap, TrendingUp, DollarSign, Clock, Users } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════
//  CONFIGURACIÓN
// ══════════════════════════════════════════════════════════════════
const PHONE = '584123555747';
const WA_MSG = encodeURIComponent('Hola, quiero el Masterkit Elite por $19 a Tasa BCV. ¿Cómo pago con Pago Móvil?');
const WA_URL = `https://wa.me/${PHONE}?text=${WA_MSG}`;
const CHECKOUT = 'https://checkout.digitalsolvex.shop/cart/44388729454680:1';
const YT_ID = 'dSrultJNhdQ';
const YT_THUMB = `https://img.youtube.com/vi/${YT_ID}/hqdefault.jpg`;
const YT_URL = `https://youtu.be/${YT_ID}`;
const PIXEL_ID = '951909037158376';

// ══════════════════════════════════════════════════════════════════
//  CSS KEYFRAMES GLOBALES
// ══════════════════════════════════════════════════════════════════
function GlobalStyles() {
  return <style data-id="element-0" data-mp="3mjztd6hzp62c">{`
      @keyframes blob {
        0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; transform:translate(0,0) scale(1); }
        25%      { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; transform:translate(-25px,20px) scale(1.05); }
        50%      { border-radius:50% 60% 30% 60%/30% 50% 60% 40%; transform:translate(20px,-15px) scale(0.95); }
        75%      { border-radius:40% 60% 60% 30%/60% 40% 50% 40%; transform:translate(-10px,25px) scale(1.02); }
      }
      @keyframes shimmer {
        0%   { transform:translateX(-150%); }
        100% { transform:translateX(250%); }
      }
      @keyframes float {
        0%,100% { transform:translateY(0px); }
        50%     { transform:translateY(-12px); }
      }
      @keyframes gradient-x {
        0%,100% { background-position:0% 50%; }
        50%     { background-position:100% 50%; }
      }
      @keyframes ping-ring {
        0%   { transform:scale(1); opacity:.6; }
        100% { transform:scale(2.2); opacity:0; }
      }
      @keyframes grid-pulse {
        0%,100% { opacity:.03; }
        50%     { opacity:.06; }
      }
      .blob-1 { animation: blob 9s ease-in-out infinite; }
      .blob-2 { animation: blob 11s ease-in-out infinite 2s; }
      .blob-3 { animation: blob 13s ease-in-out infinite 4.5s; }
      .float-anim { animation: float 4s ease-in-out infinite; }
      .float-slow  { animation: float 6s ease-in-out infinite 1s; }
      .gradient-animate {
        background-size: 200% 200%;
        animation: gradient-x 3.5s ease infinite;
      }
      .shimmer-wrap { position:relative; overflow:hidden; }
      .shimmer-wrap::after {
        content:'';
        position:absolute;
        top:0; left:0;
        width:50%; height:100%;
        background:linear-gradient(90deg,transparent,rgba(255,255,255,.18),transparent);
        animation:shimmer 2.8s ease-in-out infinite;
        pointer-events:none;
      }
      .grid-bg {
        background-image:
          linear-gradient(rgba(255,255,255,.04) 1px,transparent 1px),
          linear-gradient(90deg,rgba(255,255,255,.04) 1px,transparent 1px);
        background-size:48px 48px;
        animation: grid-pulse 6s ease-in-out infinite;
      }
      .ring-ping {
        position:absolute; inset:-4px;
        border-radius:inherit;
        border:2px solid #25D366;
        animation: ping-ring 2s ease-out infinite;
      }
      .ring-ping-2 {
        position:absolute; inset:-4px;
        border-radius:inherit;
        border:2px solid #25D366;
        animation: ping-ring 2s ease-out infinite .7s;
      }
    `}</style>;
}

// ══════════════════════════════════════════════════════════════════
//  COMPONENTES DE UTILIDAD
// ══════════════════════════════════════════════════════════════════

/** Número animado que cuenta desde 0 cuando entra en pantalla */
function CountUp({
  to,
  suffix = ''
}: {
  to: number;
  suffix?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true
  });
  const count = useMotionValue(0);
  const rounded = useTransform(count, v => Math.round(v));
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const unsub = rounded.on('change', v => setDisplay(v));
    return unsub;
  }, [rounded]);
  useEffect(() => {
    if (inView) animate(count, to, {
      duration: 1.8,
      ease: 'easeOut'
    });
  }, [inView, count, to]);
  return <span ref={ref} data-id="element-1" data-mp="14wetsqber4ar">{display}{suffix}</span>;
}

/** Botón WhatsApp con shimmer + anillos de ping */
function BtnWhatsApp({
  label = 'Comprar por WhatsApp — $19 a Tasa BCV',
  size = 'lg'
}: {
  label?: string;
  size?: 'lg' | 'sm';
}) {
  const lg = 'py-4 px-8 text-lg font-black';
  const sm = 'py-3 px-6 text-sm font-bold';
  return <motion.a href={WA_URL} target="_blank" rel="noopener noreferrer" whileHover={{
    scale: 1.03,
    boxShadow: '0 0 48px rgba(37,211,102,.65)'
  }} whileTap={{
    scale: 0.97
  }} onClick={() => (window as any).fbq?.('track', 'Contact')} className={`shimmer-wrap relative flex items-center justify-center gap-2.5 rounded-xl w-full
        bg-[#25D366] hover:bg-[#1db954] text-white transition-colors duration-200
        shadow-[0_0_30px_rgba(37,211,102,0.45)] ${size === 'lg' ? lg : sm}`} style={{
    isolation: 'isolate'
  }} data-id="element-2" data-mp="3koubf1ihda52">
      <span className="ring-ping" data-id="element-3" data-mp="2ev6iw6s2by9w" />
      <span className="ring-ping-2" data-id="element-4" data-mp="2eu4vr3xrvlbt" />
      <MessageCircle className="w-5 h-5 flex-shrink-0 relative z-10" data-id="element-5" data-mp="2ex0xl3cqqu5j" />
      <span className="relative z-10" data-id="element-6" data-mp="2jsg57dk5ntti">{label}</span>
    </motion.a>;
}

/** Botón checkout — Pago Móvil, Tarjeta, Binance */
function BtnCheckout() {
  return <div className="flex flex-col gap-1.5" data-id="element-7" data-mp="2sv7izk0i71ob">
      <motion.a href={CHECKOUT} target="_blank" rel="noopener noreferrer" whileHover={{
      scale: 1.02,
      borderColor: 'rgba(255,255,255,.3)'
    }} whileTap={{
      scale: 0.98
    }} onClick={() => (window as any).fbq?.('track', 'InitiateCheckout')} className="flex items-center justify-center gap-2 w-full py-3 px-6 text-sm font-semibold
          text-gray-300 hover:text-white border border-white/12 hover:border-white/25 rounded-xl transition-all duration-200" data-id="element-8" data-mp="2stwh5pqmc3zu">
        <CreditCard className="w-4 h-4" data-id="element-9" data-mp="2attmg30qldh8" />
        Pagar con Pago Móvil · Tarjeta · Binance
      </motion.a>
      <p className="text-center text-gray-600 text-[11px]" data-id="element-10" data-mp="25rulezsgysng">
        Selecciona tu método de pago en el checkout → Pago Móvil disponible como opción
      </p>
    </div>;
}

/** Thumbnail del video — abre YouTube en nueva pestaña */
function VideoThumb() {
  return <motion.a href={YT_URL} target="_blank" rel="noopener noreferrer" whileHover={{
    scale: 1.02
  }} className="relative w-full aspect-video rounded-2xl overflow-hidden group cursor-pointer bg-[#0a0a0a] block" data-id="element-11" data-mp="2thoxhf4ysd6n">
      <img src={YT_THUMB} alt="Demo Masterkit Elite" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-12" data-mp="33etllkideiq3" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent group-hover:from-black/40 transition-all" data-id="element-13" data-mp="3i3d58yg2kjzi" />
      <div className="absolute inset-0 flex items-center justify-center" data-id="element-14" data-mp="3n1y5gxvbn3ny">
        <motion.div whileHover={{
        scale: 1.12
      }} className="w-18 h-18 w-[72px] h-[72px] bg-white/95 rounded-full flex items-center justify-center shadow-2xl" data-id="element-15" data-mp="384nlt18iyrh5">
          <Play className="w-7 h-7 text-black ml-1.5" fill="black" data-id="element-16" data-mp="2zc3vco2dzt7e" />
        </motion.div>
      </div>
      <div className="absolute bottom-4 inset-x-0 text-center" data-id="element-17" data-mp="344wzpaty0ziy">
        <span className="text-xs text-white/90 bg-black/55 px-4 py-1.5 rounded-full backdrop-blur-sm" data-id="element-18" data-mp="2pez96o8jjxql">
          ▶ Ver cómo funciona la Calculadora Anti-inflación
        </span>
      </div>
    </motion.a>;
}

/** FAQ accordion */
function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  const items = [{
    q: '¿Cómo pago con Pago Móvil?',
    a: 'Haz clic en el botón verde de WhatsApp y te envío los datos de Pago Móvil al instante. Confirmo tu pago y recibes el Masterkit en menos de 5 minutos. También acepto Binance Pay y tarjetas internacionales.'
  }, {
    q: '¿Funciona si no me llevo bien con la tecnología?',
    a: 'Sí. Está diseñado para ser tan fácil como anotar en un cuaderno. Incluye manual paso a paso y soporte directo por WhatsApp. Si te trancas en algo, te ayudo hasta que lo domines.'
  }, {
    q: '¿Tengo que pagar mensualidades o por cada computadora?',
    a: 'No. Un solo pago de $19 y puedes copiarlo en todas las computadoras que necesites — tus cajas de cobro, la laptop de la casa. Sin licencias, sin mensualidades. El archivo es tuyo de por vida.'
  }];
  return <div className="space-y-3" data-id="element-19" data-mp="238522oin887c">
      {items.map((item, i) => <motion.div key={i} initial={{
      opacity: 0,
      y: 12
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      delay: i * 0.08
    }} className={`border rounded-2xl overflow-hidden transition-all duration-300
            ${open === i ? 'border-[#25D366]/40 bg-[#25D366]/5' : 'border-white/8 bg-[#0a0a0a] hover:border-white/15'}`} data-id="element-20" data-mp="235se9uq7f3md" data-mpi={i}>
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left gap-4" data-id="element-21" data-mp="dowtby9t6lu4">
            <span className={`font-bold text-base transition-colors ${open === i ? 'text-[#25D366]' : 'text-white'}`} data-id="element-22" data-mp="np77pm7kxzav">
              {item.q}
            </span>
            <motion.div animate={{
          rotate: open === i ? 180 : 0
        }} transition={{
          duration: 0.25
        }} data-id="element-23" data-mp="3m485et7bqcs6">
              <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" data-id="element-24" data-mp="3r3bcskas0v3b" />
            </motion.div>
          </button>
          <AnimatePresence initial={false} data-id="element-25" data-mp="4ptamvek08g5">
            {open === i && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.28
        }} data-id="element-26" data-mp="4rw8u2pv17vy">
                <p className="px-5 pb-5 text-gray-400 text-sm leading-relaxed border-t border-white/8 pt-3" data-id="element-27" data-mp="38dbnbwb0xdzd">{item.a}</p>
              </motion.div>}
          </AnimatePresence>
        </motion.div>)}
    </div>;
}

// ══════════════════════════════════════════════════════════════════
//  META PIXEL
// ══════════════════════════════════════════════════════════════════
function initPixel() {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (w.fbq) return;
  const n = w.fbq = function (...a: any[]) {
    n.callMethod ? n.callMethod(...a) : n.queue.push(a);
  };
  w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(s);
  w.fbq('init', PIXEL_ID);
  w.fbq('track', 'PageView');
}

// ══════════════════════════════════════════════════════════════════
//  APP
// ══════════════════════════════════════════════════════════════════
const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 22
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export default function App() {
  useEffect(() => {
    initPixel();
  }, []);
  const beneficios = ['Calculadora Anti-inflación — actualizas la tasa BCV y todos tus precios se ajustan solos', 'Control de Inventario — registras facturas de proveedor y el stock se suma automáticamente', 'Estructura de Costos — sabe exactamente cuánto te costó cada producto y cuánto cobrar', 'Registro de Ventas — cada venta descuenta el stock en tiempo real', 'Cierre de Caja en 2 minutos — cuánto vendiste en $ y Bs, y tu ganancia real del día', 'Dashboard con KPIs — tus 5 productos más vendidos y punto de equilibrio automático', 'Manual de usuario paso a paso + Soporte por WhatsApp incluido'];
  const testimonios = [{
    n: 'Roberto C.',
    r: 'Repuestos Automotrices',
    i: 'RC',
    c: 'from-cyan-500 to-blue-600',
    t: 'Controlar el inventario era un caos. Ahora sé exactamente qué tengo y qué cobrar. Las facturas se suman solas. Vale cada centavo.'
  }, {
    n: 'Andrea P.',
    r: 'Minimarket (3 Cajas)',
    i: 'AP',
    c: 'from-emerald-400 to-green-600',
    t: 'Lo instalé en mis 3 computadoras y funciona perfecto con la tiquetera. Los tickets los mando por WhatsApp. Súper profesional.'
  }, {
    n: 'Carlos V.',
    r: 'Bodega y Purificadora',
    i: 'CV',
    c: 'from-pink-500 to-rose-600',
    t: 'Le tenía miedo a la tecnología pero los tutoriales son claros. Me dieron soporte por WhatsApp. Ya sé cuánto gano cada día exacto.'
  }];
  return <div className="min-h-screen bg-[#060608] text-white font-sans overflow-x-hidden selection:bg-[#25D366]/30" data-id="element-28" data-mp="2pkkpx4osha9n">
      <GlobalStyles data-id="element-29" data-mp="2tlwgbcfhlziw" />

      {/* ══ HERO ════════════════════════════════════════════════════ */}
      <section className="relative pt-16 pb-12 md:pt-24 md:pb-20 px-4 md:px-8 overflow-hidden" data-id="element-30" data-mp="3hc0akt6scmx8">

        {/* Fondo: grid sutil */}
        <div className="grid-bg absolute inset-0 -z-20 pointer-events-none" data-id="element-31" data-mp="3m5mlogxawiqx" />

        {/* Blobs animados */}
        <div className="blob-1 absolute top-[-8%] left-[-8%] w-[480px] h-[480px] bg-[#25D366]/12 -z-10 pointer-events-none" data-id="element-32" data-mp="1am6s9s393o81" />
        <div className="blob-2 absolute bottom-[-5%] right-[-5%] w-[380px] h-[380px] bg-cyan-500/10 -z-10 pointer-events-none" data-id="element-33" data-mp="1kevxi587k5ux" />
        <div className="blob-3 absolute top-[35%] right-[15%] w-[260px] h-[260px] bg-amber-500/6 -z-10 pointer-events-none" data-id="element-34" data-mp="1fgknnuhwxvcp" />

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-35" data-mp="rrrq5tln7ffz">

          {/* Columna texto */}
          <motion.div initial="hidden" animate="show" variants={stagger} data-id="element-36" data-mp="11oe9gdyblyfw">

            {/* Badge animado */}
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                bg-[#25D366]/12 border border-[#25D366]/25 text-[#25D366] text-xs font-bold mb-6 uppercase tracking-widest" data-id="element-37" data-mp="woidn1zs1dbf">
              <span className="relative flex h-2 w-2" data-id="element-38" data-mp="2861cki6bqu46">
                <span className="animate-ping absolute inset-0 rounded-full bg-[#25D366] opacity-70" data-id="element-39" data-mp="hnv3um3w3rcd" />
                <span className="relative rounded-full h-2 w-2 bg-[#25D366]" data-id="element-40" data-mp="mmxvn4uv012e" />
              </span>
              Calculadora Anti-inflación · Masterkit Elite
            </motion.div>

            {/* H1 con gradiente animado */}
            <motion.h1 variants={fadeUp} className="font-black text-4xl md:text-5xl lg:text-[3.1rem] leading-[1.1] tracking-tight mb-5" style={{
            fontFamily: "'Poppins',sans-serif"
          }} data-id="element-41" data-mp="vnw3xzcbyuqs">
              Si cobras en bolívares sin saber tu margen en dólares,{' '}
              <span className="text-transparent bg-clip-text gradient-animate" style={{
              backgroundImage: 'linear-gradient(90deg,#f87171,#fb923c,#fbbf24,#f87171)',
              backgroundSize: '200%'
            }} data-id="element-42" data-mp="1ac0pdb2rpkd9">
                estás perdiendo plata todos los días.
              </span>
            </motion.h1>

            <motion.p variants={fadeUp} className="text-gray-300 text-lg leading-relaxed mb-8 max-w-lg font-light" data-id="element-43" data-mp="1o95qqxrjdv26">
              El <strong className="text-white font-semibold" data-id="element-44" data-mp="1tg3uxszm6iua">Masterkit Elite</strong> calcula exactamente cuánto cobrar según la tasa BCV del día. Inventario, costos, fiados y cierre de caja — para Minimarkets, Bodegas, Repuestos y Licorerías.
            </motion.p>

            {/* Precio flotante */}
            <motion.div variants={fadeUp} className="flex items-baseline gap-3 mb-7" data-id="element-45" data-mp="2h5oo14phewha">
              <motion.div animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 3.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative" data-id="element-46" data-mp="2h7j15wuihkat">
                <div className="absolute -inset-3 bg-[#25D366]/15 rounded-2xl blur-xl" data-id="element-47" data-mp="23e9uw51v1ewi" />
                <span className="relative text-5xl font-black text-white" style={{
                fontFamily: "'Poppins',sans-serif",
                textShadow: '0 0 30px rgba(37,211,102,.4)'
              }} data-id="element-48" data-mp="23cgx7f98650n">$19</span>
              </motion.div>
              <div data-id="element-49" data-mp="2v3mr3dycr6hh">
                <p className="text-[#25D366] font-bold text-sm" data-id="element-50" data-mp="t876wn0v6t0n">Pago único · Sin mensualidades</p>
                <p className="text-amber-400 text-xs font-semibold" data-id="element-51" data-mp="y6qu0qr72aul">Precio en Bs a Tasa BCV oficial del día</p>
                <p className="text-gray-500 text-xs" data-id="element-52" data-mp="jinnng98ox59">Entrega en menos de 5 minutos</p>
              </div>
            </motion.div>

            {/* CTAs */}
            <motion.div variants={fadeUp} className="flex flex-col gap-3 max-w-md mb-7" data-id="element-53" data-mp="of48o1l56ve4">
              <BtnWhatsApp data-id="element-54" data-mp="ag4jmstpwehf" />
              <BtnCheckout data-id="element-55" data-mp="fgitmgevpyi3" />
            </motion.div>

            {/* Social proof pills */}
            <motion.div variants={fadeUp} className="flex flex-wrap gap-x-5 gap-y-2" data-id="element-56" data-mp="fe651kem8pw2">
              {[{
              icon: ShieldCheck,
              c: 'text-[#25D366]',
              t: 'Pago único de por vida'
            }, {
              icon: Star,
              c: 'text-yellow-400',
              t: '4.9/5 valoración'
            }, {
              icon: Zap,
              c: 'text-cyan-400',
              t: 'Entrega en < 5 min'
            }, {
              icon: Users,
              c: 'text-purple-400',
              t: '87+ negocios activos'
            }].map(({
              icon: Icon,
              c,
              t
            }, i) => <div key={i} className="flex items-center gap-1.5 text-xs text-gray-500" data-id="element-57" data-mp="3kc22kdr52rxb" data-mpi={i}>
                  <Icon className={`w-3.5 h-3.5 ${c}`} data-id="element-58" data-mp="3fd8punjof4y7" />
                  {t}
                </div>)}
            </motion.div>
          </motion.div>

          {/* Video con borde animado */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="relative float-slow" data-id="element-59" data-mp="3pbofsn6yw864">
            <div className="absolute -inset-1 bg-gradient-to-tr from-[#25D366]/40 via-cyan-500/20 to-amber-500/20 rounded-3xl blur-xl opacity-60" data-id="element-60" data-mp="gxfve2elmnp1" />
            <div className="relative border border-white/10 rounded-3xl overflow-hidden p-2 bg-[#0d0d0d]
              shadow-[0_0_60px_rgba(37,211,102,.15),0_0_120px_rgba(34,211,238,.08)]" data-id="element-61" data-mp="bz4l5qke6b6a">
              <VideoThumb data-id="element-62" data-mp="2dr9ijhknu0oe" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ STATS BAR ═══════════════════════════════════════════════ */}
      <section className="py-8 border-y border-white/6 bg-[#080808] relative overflow-hidden" data-id="element-63" data-mp="1wvdfs0j9dg28">
        <div className="absolute inset-0 bg-gradient-to-r from-[#25D366]/5 via-transparent to-cyan-500/5 pointer-events-none" data-id="element-64" data-mp="21m2arpu5cv64" />
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center relative z-10" data-id="element-65" data-mp="26kdk7zgj53nt">
          {[{
          icon: Users,
          val: 87,
          suf: '+',
          label: 'Negocios activos'
        }, {
          icon: Star,
          val: 49,
          suf: '/5',
          label: 'Valoración'
        }, {
          icon: DollarSign,
          val: 0,
          suf: ' mensualidades',
          label: 'Costo recurrente'
        }, {
          icon: Clock,
          val: 5,
          suf: ' min',
          label: 'Tiempo de entrega'
        }].map(({
          icon: Icon,
          val,
          suf,
          label
        }, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} whileHover={{
          scale: 1.05
        }} className="flex flex-col items-center gap-1 p-3 rounded-2xl hover:bg-white/5 transition-colors cursor-default" data-id="element-66" data-mp="2uarhc4dz1sp9" data-mpi={i}>
              <Icon className="w-5 h-5 text-[#25D366] mb-1" data-id="element-67" data-mp="dwnx3a1g3rom" />
              <span className="text-2xl font-black text-white" style={{
            fontFamily: "'Poppins',sans-serif"
          }} data-id="element-68" data-mp="dv4l3vmto2sn">
                {val === 49 ? <><CountUp to={4} data-id="element-69" data-mp="3z9tib0mexdq" /><span className="text-lg" data-id="element-70" data-mp="3zt2h28ambfm">.9⭐</span></> : <><CountUp to={val} data-id="element-71" data-mp="3w329v5nz9vlf" /><span className="text-sm font-normal text-gray-400" data-id="element-72" data-mp="3w2kh4j0ixzi9">{suf}</span></>}
              </span>
              <span className="text-gray-500 text-xs uppercase tracking-wide" data-id="element-73" data-mp="3r4iop7h7knu4">{label}</span>
            </motion.div>)}
        </div>
      </section>

      {/* ══ DOLOR ════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 px-4 md:px-8 relative overflow-hidden" data-id="element-74" data-mp="1pa0762nst6j8">
        <div className="blob-3 absolute top-0 right-0 w-[350px] h-[350px] bg-rose-500/6 pointer-events-none -z-10" data-id="element-75" data-mp="1ke2p9rwvzs4c" />
        <div className="max-w-4xl mx-auto text-center mb-10" data-id="element-76" data-mp="2c96uh5meiw3z">
          <motion.div initial={{
          opacity: 0,
          scale: .9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} data-id="element-77" data-mp="279kewpj3p3s0">
            <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/15 text-rose-400 text-xs font-bold uppercase tracking-widest mb-5" data-id="element-78" data-mp="22fq2m75trr2y">
              🚨 La realidad del comerciante venezolano
            </span>
            <h2 className="text-2xl md:text-3xl font-black text-white leading-tight" style={{
            fontFamily: "'Poppins',sans-serif"
          }} data-id="element-79" data-mp="1ohi5nz8sgprs">
              Si esto te pasa a diario,{' '}
              <span className="text-rose-400" data-id="element-80" data-mp="1eju16hlds3wf">el negocio está perdiendo dinero</span>
            </h2>
          </motion.div>
        </div>

        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-81" data-mp="3dvep6xf62o38">
          {[{
          e: '📉',
          t: 'Perdés plata con la tasa',
          d: 'No tienes tus costos en dólares claros. Cuando el BCV sube, cobras de menos sin darte cuenta.'
        }, {
          e: '📦',
          t: 'No sabes cuánto tienes',
          d: 'Tu inventario vive en tu cabeza o en un cuaderno. Compras de más o te quedas sin lo que más vende.'
        }, {
          e: '🧮',
          t: 'Calculas todo a mano',
          d: 'Sacas la calculadora cada vez que entra un cliente. Lento, impreciso y con riesgo de error.'
        }].map(({
          e,
          t,
          d
        }, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.12
        }} whileHover={{
          y: -6,
          boxShadow: '0 20px 40px rgba(248,113,113,.12)'
        }} className="group bg-[#0a0a0a] border border-white/6 rounded-2xl p-6 cursor-default
                hover:border-rose-500/30 transition-all duration-300 relative overflow-hidden" data-id="element-82" data-mp="2ygt262hgwpqp" data-mpi={i}>
              <div className="absolute inset-0 bg-gradient-to-br from-rose-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none" data-id="element-83" data-mp="rp1sq5d3xq1d" />
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300 inline-block" data-id="element-84" data-mp="3t7h6yzcervob">{e}</div>
              <h3 className="text-white font-bold text-lg mb-2" data-id="element-85" data-mp="bzvhke8pbj4s">{t}</h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-86" data-mp="70972ck9cgzo">{d}</p>
            </motion.div>)}
        </div>
      </section>

      {/* ══ QUÉ INCLUYE ══════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-[#050507] border-y border-white/5 relative overflow-hidden" data-id="element-87" data-mp="3ah7sor3ckdg9">
        <div className="blob-1 absolute -bottom-20 -left-20 w-[300px] h-[300px] bg-[#25D366]/8 pointer-events-none -z-10" data-id="element-88" data-mp="3p6448y4m48lm" />
        <div className="max-w-3xl mx-auto" data-id="element-89" data-mp="3kdkp3wqa15ep">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-90" data-mp="2wpkv8nxo4yvw">
            <h2 className="text-2xl md:text-3xl font-black text-white mb-3" style={{
            fontFamily: "'Poppins',sans-serif"
          }} data-id="element-91" data-mp="2rpyjyk1ehspq">
              Todo lo que incluye el{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#25D366] to-cyan-400" data-id="element-92" data-mp="1bzfjygwqf645">
                Masterkit Elite Básico
              </span>
            </h2>
            <p className="text-gray-500 text-sm" data-id="element-93" data-mp="12930ks0nlizl">Un solo archivo Excel · Sin instalaciones · Sin internet constante</p>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10" data-id="element-94" data-mp="1q08dozf7lx67">
            {beneficios.map((b, i) => <motion.div key={i} variants={fadeUp} whileHover={{
            x: 4,
            backgroundColor: 'rgba(37,211,102,.06)',
            borderColor: 'rgba(37,211,102,.2)'
          }} className="flex items-start gap-3 bg-[#0a0a0a] border border-white/6 rounded-xl p-4 transition-all duration-200 cursor-default" data-id="element-95" data-mp="1gxz7glqz4pym" data-mpi={i}>
                <motion.div initial={{
              scale: 0
            }} whileInView={{
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.07,
              type: 'spring',
              stiffness: 200
            }} data-id="element-96" data-mp="9ikpp4toudbm">
                  <CheckCircle2 className="w-4 h-4 text-[#25D366] flex-shrink-0 mt-0.5" data-id="element-97" data-mp="kzscs6mb8wf5" />
                </motion.div>
                <span className="text-gray-300 text-sm leading-snug" data-id="element-98" data-mp="fzxidt3btopc">{b}</span>
              </motion.div>)}
          </motion.div>

          <div className="flex flex-col gap-3 max-w-md mx-auto" data-id="element-99" data-mp="18qxr2hclrgnc">
            <BtnWhatsApp label="Quiero el Masterkit por $19 a Tasa BCV →" data-id="element-100" data-mp="18obicedz6v3j" />
            <BtnCheckout data-id="element-101" data-mp="13se2082ppoqr" />
          </div>
        </div>
      </section>

      {/* ══ BONOS GRATUITOS ══════════════════════════════════════════ */}
      <section className="py-16 md:py-20 px-4 md:px-8 relative overflow-hidden" data-id="element-102" data-mp="urfzju7x9paa">
        <div className="blob-2 absolute top-0 right-0 w-[350px] h-[350px] bg-amber-500/6 pointer-events-none -z-10" data-id="element-103" data-mp="3jiqwq83xihkk" />
        <div className="max-w-4xl mx-auto" data-id="element-104" data-mp="3fgm49cp7vqzi">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-105" data-mp="3ffu7hyjvvn7n">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-widest mb-5" data-id="element-106" data-mp="1z06msppkegc4">
              🎁 Bonos exclusivos — incluidos en tu compra
            </span>
            <h2 className="text-2xl md:text-3xl font-black text-white" style={{
            fontFamily: "'Poppins',sans-serif"
          }} data-id="element-107" data-mp="2dpkwy00yt1sk">
              Y además te llevas estos bonos{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-[#25D366]" data-id="element-108" data-mp="2imuih4l8gtb4">
                100% GRATIS
              </span>
            </h2>
            <p className="text-gray-500 text-sm mt-3" data-id="element-109" data-mp="2roavlk3fn9j1">Solo por adquirir tu Masterkit hoy — sin costo adicional</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-110" data-mp="secgeswmdg66">
            {/* Bono 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} whileHover={{
            y: -6,
            boxShadow: '0 20px 40px rgba(250,204,21,.1)'
          }} transition={{
            delay: 0.05
          }} className="group relative bg-[#0a0a0a] border border-white/8 rounded-2xl overflow-hidden hover:border-amber-500/30 transition-all duration-300" data-id="element-111" data-mp="xdfa5h75fzyu">
              <div className="absolute top-4 right-4 z-20" data-id="element-112" data-mp="24mis6odswhfi">
                <span className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black text-xs font-black px-3 py-1 rounded-full shadow-lg transform rotate-2" data-id="element-113" data-mp="2jk2pd94gh8b4">
                  ¡GRATIS HOY!
                </span>
              </div>
              <div className="relative h-44 overflow-hidden border-b border-white/8" data-id="element-114" data-mp="2scmvsvqzoh6i">
                <img src="https://cdn.shopify.com/s/files/1/0708/2174/0632/files/100_plantillas_de_Canva.jpg?v=1785156268" alt="100 Plantillas Canva Pro para Negocios" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" data-id="element-115" data-mp="2occn3bbtbcny" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent opacity-70" data-id="element-116" data-mp="3lx44z943woj6" />
              </div>
              <div className="p-6" data-id="element-117" data-mp="36zbtcs1okka9">
                <p className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-1" data-id="element-118" data-mp="372hdrtzd6xzn">Bono 1</p>
                <h3 className="text-white font-black text-lg mb-2" style={{
                fontFamily: "'Poppins',sans-serif"
              }} data-id="element-119" data-mp="2y7tauah9hse0">
                  100 Plantillas Pro (Canva) para Negocios
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4" data-id="element-120" data-mp="330crjgb8x3n1">
                  Olvídate de pagar diseñadores. 50 posts y 50 historias diseñadas para detener el scroll y atraer clientes a tu local.
                </p>
                <div className="flex items-center gap-3 bg-white/5 border border-white/8 rounded-xl px-4 py-2.5 w-fit" data-id="element-121" data-mp="2f8fghrfmefmz">
                  <span className="text-gray-500 text-xs" data-id="element-122" data-mp="2j991abxrnebn">Valorado en:</span>
                  <span className="text-rose-400 line-through font-bold text-base" data-id="element-123" data-mp="2zql431jh2o4c">$47</span>
                  <span className="text-[#25D366] font-bold text-sm" data-id="element-124" data-mp="2uqfcfld7d1m9">→ GRATIS</span>
                </div>
              </div>
            </motion.div>

            {/* Bono 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} whileHover={{
            y: -6,
            boxShadow: '0 20px 40px rgba(250,204,21,.1)'
          }} transition={{
            delay: 0.12
          }} className="group relative bg-[#0a0a0a] border border-white/8 rounded-2xl overflow-hidden hover:border-amber-500/30 transition-all duration-300" data-id="element-125" data-mp="2ksstmnss8px0">
              <div className="absolute top-4 right-4 z-20" data-id="element-126" data-mp="26z1g74bxklw0">
                <span className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black text-xs font-black px-3 py-1 rounded-full shadow-lg transform rotate-2" data-id="element-127" data-mp="223ma4yk5zua5">
                  ¡GRATIS HOY!
                </span>
              </div>
              <div className="relative h-44 overflow-hidden border-b border-white/8" data-id="element-128" data-mp="1t3z582s886cc">
                <img src="https://cdn.shopify.com/s/files/1/0708/2174/0632/files/Scripts_de_cierre_Ventas.jpg?v=1785156088" alt="Pack de Scripts de Venta por WhatsApp" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" data-id="element-129" data-mp="8ln8yeht7eg5" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent opacity-70" data-id="element-130" data-mp="3l1hbp05he5et" />
              </div>
              <div className="p-6" data-id="element-131" data-mp="3q0btpzvmjpxv">
                <p className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-1" data-id="element-132" data-mp="3uzf1vt6wica2">Bono 2</p>
                <h3 className="text-white font-black text-lg mb-2" style={{
                fontFamily: "'Poppins',sans-serif"
              }} data-id="element-133" data-mp="3k7pgqva5fzv">
                  Pack de Scripts de Venta (WhatsApp)
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4" data-id="element-134" data-mp="379kmb3v769m4">
                  La ruta exacta para manejar objeciones. Sabrás qué decir para que dejen de decirte "está caro" y empiecen a pedirte los datos de pago.
                </p>
                <div className="flex items-center gap-3 bg-white/5 border border-white/8 rounded-xl px-4 py-2.5 w-fit" data-id="element-135" data-mp="2lxf5z5k12o8w">
                  <span className="text-gray-500 text-xs" data-id="element-136" data-mp="2811r846vp0y4">Valorado en:</span>
                  <span className="text-rose-400 line-through font-bold text-base" data-id="element-137" data-mp="2bz9bh90jwke2">$19</span>
                  <span className="text-[#25D366] font-bold text-sm" data-id="element-138" data-mp="1y8dwdezgl721">→ GRATIS</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Valor total percibido */}
          <motion.div initial={{
          opacity: 0,
          scale: .97
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="bg-gradient-to-r from-amber-500/8 to-[#25D366]/8 border border-amber-500/20 rounded-2xl p-5 text-center" data-id="element-139" data-mp="1ocasa3l1qycg">
            <p className="text-gray-400 text-sm mb-1" data-id="element-140" data-mp="1k9egowtxc2i0">Valor total de lo que recibes hoy</p>
            <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-141" data-mp="1kb7j1zxi80k7">
              <span className="text-gray-500 text-lg line-through" data-id="element-142" data-mp="10pp2xwso5g9f">Masterkit $24.99 + Bonos $66</span>
              <span className="text-2xl font-black text-white" style={{
              fontFamily: "'Poppins',sans-serif"
            }} data-id="element-143" data-mp="vqm9zapyr0ht">
                Todo por solo <span className="text-[#25D366]" data-id="element-144" data-mp="15mpcwisn335t">$19</span>
              </span>
              <span className="text-amber-400 text-xs font-bold" data-id="element-145" data-mp="15os6tdwwxgft">a Tasa BCV del día</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ TESTIMONIOS ══════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 px-4 md:px-8 relative overflow-hidden" data-id="element-146" data-mp="1tcrx6cqbb4tv">
        <div className="blob-2 absolute top-0 left-1/2 w-[400px] h-[400px] bg-cyan-500/6 pointer-events-none -z-10 -translate-x-1/2" data-id="element-147" data-mp="1od6xg03bisg3" />
        <div className="max-w-5xl mx-auto" data-id="element-148" data-mp="22dzx0z8h8dq2">
          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl md:text-3xl font-black text-white text-center mb-10" style={{
          fontFamily: "'Poppins',sans-serif"
        }} data-id="element-149" data-mp="22avh54lnftjk">
            Negocios venezolanos que ya{' '}
            <span className="text-[#25D366]" data-id="element-150" data-mp="zzs8yxqmgtp5">tomaron el control</span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-151" data-mp="13zuczdjtxz7l">
            {testimonios.map((t, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.13
          }} whileHover={{
            y: -8,
            boxShadow: '0 24px 48px rgba(37,211,102,.1)'
          }} className="bg-[#0a0a0a] border border-white/6 rounded-2xl p-6 hover:border-[#25D366]/25 transition-all duration-300 cursor-default" data-id="element-152" data-mp="18tykpz0x6b9q" data-mpi={i}>
                <div className="flex gap-0.5 mb-4" data-id="element-153" data-mp="3pghjydsmetja">
                  {[...Array(5)].map((_, j) => <motion.div key={j} initial={{
                opacity: 0,
                scale: 0
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1 + j * 0.06,
                type: 'spring'
              }} data-id="element-154" data-mp="3ti35son4ui5m" data-mpi={j}>
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-155" data-mp="3h7bt40p4s6sf" />
                    </motion.div>)}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-5" data-id="element-156" data-mp="3c90ihnr0hs95">"{t.t}"</p>
                <div className="flex items-center gap-3 border-t border-white/6 pt-4" data-id="element-157" data-mp="388qdoeeyh7vv">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.c} flex items-center justify-center flex-shrink-0 shadow-lg`} data-id="element-158" data-mp="8qqsb76nkk0e">
                    <span className="text-xs font-bold text-white" data-id="element-159" data-mp="3r4g912k99t6">{t.i}</span>
                  </div>
                  <div data-id="element-160" data-mp="3qxn94lm3a6fo">
                    <div className="flex items-center gap-1" data-id="element-161" data-mp="3qy6gj8e45cfg">
                      <p className="text-white font-bold text-sm" data-id="element-162" data-mp="3qzzocr0gq2p1">{t.n}</p>
                      <BadgeCheck className="w-3.5 h-3.5 text-cyan-400" data-id="element-163" data-mp="2kj5t5r35m2kh" />
                    </div>
                    <p className="text-gray-500 text-xs" data-id="element-164" data-mp="2fgwzstmdcyhu">{t.r}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ══ FAQ ══════════════════════════════════════════════════════ */}
      <section className="py-14 md:py-20 px-4 md:px-8 bg-[#050507] border-y border-white/5" data-id="element-165" data-mp="3ufaqiecs043m">
        <div className="max-w-2xl mx-auto" data-id="element-166" data-mp="29xe1pnghevy">
          <motion.h2 initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl font-black text-white text-center mb-8" style={{
          fontFamily: "'Poppins',sans-serif"
        }} data-id="element-167" data-mp="2ays82zrqhhw">
            Preguntas <span className="text-[#25D366]" data-id="element-168" data-mp="gzv3sa11acn9">frecuentes</span>
          </motion.h2>
          <FAQ data-id="element-169" data-mp="lyoa9ihwl3dx" />
        </div>
      </section>

      {/* ══ CIERRE FINAL ═════════════════════════════════════════════ */}
      <section className="py-16 md:py-24 px-4 md:px-8 relative overflow-hidden" data-id="element-170" data-mp="31parux8vbn97">
        <div className="blob-1 absolute inset-0 w-full h-full bg-[#25D366]/5 -z-10 pointer-events-none" style={{
        borderRadius: 0,
        animation: 'none'
      }} data-id="element-171" data-mp="36ox35154ytfd" />
        <div className="blob-2 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#25D366]/8 -z-10 pointer-events-none" data-id="element-172" data-mp="2y6bo2fk8scyh" />

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-lg mx-auto text-center" data-id="element-173" data-mp="385u5opcojmnd">
          <div className="inline-flex items-center gap-2 bg-[#25D366]/12 border border-[#25D366]/25 rounded-full px-4 py-2 mb-6" data-id="element-174" data-mp="337sqifidk7h6">
            <ShieldCheck className="w-4 h-4 text-[#25D366]" data-id="element-175" data-mp="2ailq1azd17ta" />
            <span className="text-[#25D366] text-sm font-bold" data-id="element-176" data-mp="2ak55x0h3tguj">Soporte por WhatsApp incluido</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight" style={{
          fontFamily: "'Poppins',sans-serif"
        }} data-id="element-177" data-mp="2p91csu7fcntk">
            Toma el control de tu negocio{' '}
            <span className="text-transparent bg-clip-text gradient-animate" style={{
            backgroundImage: 'linear-gradient(90deg,#25D366,#34d399,#22d3ee,#25D366)',
            backgroundSize: '200%'
          }} data-id="element-178" data-mp="1wp2ujxthmzac">
              hoy mismo.
            </span>
          </h2>

          <p className="text-gray-400 text-base mb-8" data-id="element-179" data-mp="mzd0jbnqzgg">
            $19 a Tasa BCV del día · Pago único · Sin mensualidades
          </p>

          <div className="flex flex-col gap-3" data-id="element-180" data-mp="5fapelnl4dpw">
            <BtnWhatsApp label="Comprar el Masterkit por $19 a Tasa BCV →" data-id="element-181" data-mp="9gm7m644pwo2" />
            <BtnCheckout data-id="element-182" data-mp="38v7qm53rqbc3" />
          </div>

          <div className="mt-6 flex flex-col gap-1" data-id="element-183" data-mp="38w9dd6u5cm9n">
            <p className="text-gray-600 text-xs" data-id="element-184" data-mp="3duawtsvhinln">Pago Móvil · Binance Pay · Tarjeta Internacional</p>
            <p className="text-amber-500/50 text-xs" data-id="element-185" data-mp="3hvw48yp929og">Precio en bolívares calculado a Tasa BCV oficial del día del pago</p>
          </div>
        </motion.div>
      </section>

      {/* ══ FOOTER ════════════════════════════════════════════════════ */}
      <footer className="py-6 px-4 border-t border-white/5 bg-[#030305]" data-id="element-186" data-mp="x4vmsp6u7m68">
        <p className="text-center text-gray-600 text-xs" data-id="element-187" data-mp="1248ilzfos3ky">
          © {new Date().getFullYear()} Digital Solvex · Barquisimeto, Venezuela ·{' '}
          <a href="https://toolkit.digitalsolvex.shop" className="hover:text-gray-400 transition-colors" data-id="element-188" data-mp="1c7x1penu1gww">
            Ver todos los planes
          </a>
        </p>
      </footer>

      {/* ══ BOTÓN WA FLOTANTE ════════════════════════════════════════ */}
      <motion.a href={WA_URL} target="_blank" rel="noopener noreferrer" whileHover={{
      scale: 1.12
    }} whileTap={{
      scale: 0.95
    }} onClick={() => (window as any).fbq?.('track', 'Contact')} className="fixed bottom-5 right-5 z-[9999] group" data-id="element-189" data-mp="yi3gd6bjderx">
        <span className="absolute right-14 top-1/2 -translate-y-1/2 bg-white text-black text-xs font-bold
          py-1.5 px-3 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shadow-lg whitespace-nowrap pointer-events-none" data-id="element-190" data-mp="jjrln57q4g3e">
          ¿Dudas? Escríbeme 👋
        </span>
        <div className="relative w-[52px] h-[52px] bg-[#25D366] rounded-full flex items-center justify-center
          shadow-[0_0_24px_rgba(37,211,102,.5)]" data-id="element-191" data-mp="29wgxlsbsavyz">
          <div className="absolute inset-0 bg-[#25D366] rounded-full animate-ping opacity-25" data-id="element-192" data-mp="2t5qitci0kvfj" />
          <svg viewBox="0 0 32 32" className="w-7 h-7 text-white relative z-10" xmlns="http://www.w3.org/2000/svg" data-id="element-193" data-mp="2x6ak8s9mel7h">
            <path d="M16.002 2.186c-7.615 0-13.791 6.175-13.791 13.791 0 2.433.633 4.811 1.834 6.895l-1.956 7.143 7.309-1.917c2.046 1.096 4.35 1.674 6.605 1.674 7.612 0 13.788-6.175 13.788-13.791 0-7.615-6.176-13.795-13.789-13.795zm0 25.143c-2.062 0-4.088-.553-5.857-1.602l-.42-.249-4.355 1.142 1.161-4.246-.273-.435c-1.153-1.834-1.761-3.953-1.761-6.143 0-6.309 5.132-11.442 11.443-11.442s11.44 5.133 11.44 11.442c-.001 6.31-5.133 11.442-11.442 11.442zm6.276-8.561c-.344-.173-2.035-1.005-2.351-1.12-.315-.115-.545-.173-.775.173-.229.345-.888 1.12-1.089 1.35-.201.229-.402.258-.745.086-2.147-.946-3.69-2.095-5.123-4.577-.184-.319.18-.302.516-.973.086-.173.043-.328-.043-.501-.086-.173-.775-1.868-1.062-2.558-.279-.672-.562-.581-.775-.592-.2-.009-.43-.009-.659-.009-.229 0-.602.086-.918.432-.315.345-1.205 1.178-1.205 2.872 0 1.693 1.233 3.328 1.405 3.558.173.229 2.425 3.7 5.874 5.19 2.508 1.083 3.42.923 4.022.836.699-.101 2.035-.833 2.322-1.638.286-.805.286-1.495.201-1.638-.087-.146-.317-.233-.66-.406z" fill="currentColor" data-id="element-194" data-mp="2jdb20iigecw8" />
          </svg>
        </div>
      </motion.a>
    </div>;
}